import { Header } from '@/components/Header';
import { Footer } from '@/components/Footer';
import { SEOHead } from '@/components/SEOHead';

export const PolitiqueConfidentialitePage = () => {
  return (
    <div className="min-h-screen bg-background">
      <SEOHead 
        title="Politique de confidentialité | Homely Solutions"
        description="Politique de confidentialité et protection des données de Homely Solutions Sàrl, conformément au droit suisse (LPD)."
      />
      
      <Header />
      
      <main className="py-20">
        <div className="mx-auto w-full max-w-[var(--container)] px-4 sm:px-6 lg:px-8">
          <h1 className="text-4xl font-bold mb-8" style={{ fontFamily: 'var(--font-heading)' }}>
            Politique de confidentialité
          </h1>
          
          <div className="prose prose-lg max-w-none">
            <p className="text-lg text-muted-foreground mb-8">
              Dernière mise à jour : Mars 2026
            </p>

            <section className="mb-12">
              <h2 className="text-2xl font-semibold mb-4">1. Introduction</h2>
              <p>
                Homely Solutions Sàrl (ci-après « nous », « notre » ou « Homely Solutions ») s'engage à protéger 
                la vie privée des utilisateurs de son site internet www.homely-solutions.ch. Cette politique de 
                confidentialité explique comment nous collectons, utilisons et protégeons vos données personnelles, 
                conformément à la Loi fédérale sur la protection des données (LPD) suisse.
              </p>
            </section>

            <section className="mb-12">
              <h2 className="text-2xl font-semibold mb-4">2. Responsable du traitement</h2>
              <div className="bg-gray-50 p-6 rounded-lg">
                <p><strong>Homely Solutions Sàrl</strong></p>
                <p>Rue Jean-André Venel 4</p>
                <p>1400 Yverdon-les-Bains</p>
                <p>Suisse</p>
                <p className="mt-4"><strong>Email :</strong> <a href="mailto:contact@homely-solutions.ch" className="text-[var(--sage)] hover:underline">contact@homely-solutions.ch</a></p>
              </div>
            </section>

            <section className="mb-12">
              <h2 className="text-2xl font-semibold mb-4">3. Données collectées</h2>
              <p>Nous pouvons collecter les données suivantes :</p>
              <ul>
                <li><strong>Données d'identification :</strong> nom, prénom, adresse email, numéro de téléphone</li>
                <li><strong>Données relatives à votre demande :</strong> type de service souhaité, localisation du bien, informations sur le projet</li>
                <li><strong>Données de navigation :</strong> adresse IP, type de navigateur, pages visitées, temps passé sur le site</li>
                <li><strong>Cookies :</strong> données techniques pour améliorer votre expérience de navigation</li>
              </ul>
            </section>

            <section className="mb-12">
              <h2 className="text-2xl font-semibold mb-4">4. Finalités du traitement</h2>
              <p>Vos données sont collectées pour les finalités suivantes :</p>
              <ul>
                <li>Traiter et répondre à vos demandes de devis ou d'information</li>
                <li>Vous fournir nos services (nettoyage, conciergerie, rénovation, etc.)</li>
                <li>Améliorer notre site et nos services</li>
                <li>Respecter nos obligations légales</li>
                <li>Analyser le trafic et l'utilisation de notre site (via Google Analytics)</li>
              </ul>
            </section>

            <section className="mb-12">
              <h2 className="text-2xl font-semibold mb-4">5. Base légale du traitement</h2>
              <p>Le traitement de vos données repose sur :</p>
              <ul>
                <li><strong>Votre consentement</strong> lorsque vous remplissez un formulaire de contact</li>
                <li><strong>L'exécution d'un contrat</strong> ou de mesures précontractuelles</li>
                <li><strong>Nos intérêts légitimes</strong> pour améliorer nos services</li>
              </ul>
            </section>

            <section className="mb-12">
              <h2 className="text-2xl font-semibold mb-4">6. Destinataires des données</h2>
              <p>Vos données peuvent être partagées avec :</p>
              <ul>
                <li><strong>Notre équipe interne</strong> pour le traitement de votre demande</li>
                <li><strong>Notre CRM</strong> (Loomy) pour la gestion de la relation client</li>
                <li><strong>Google Analytics</strong> pour l'analyse du trafic (données anonymisées)</li>
                <li><strong>Nos prestataires techniques</strong> pour l'hébergement et la maintenance du site</li>
              </ul>
              <p>Nous ne vendons jamais vos données personnelles à des tiers.</p>
            </section>

            <section className="mb-12">
              <h2 className="text-2xl font-semibold mb-4">7. Durée de conservation</h2>
              <p>
                Vos données personnelles sont conservées pendant la durée nécessaire aux finalités pour lesquelles 
                elles ont été collectées, et conformément aux obligations légales de conservation en vigueur en Suisse.
              </p>
              <ul>
                <li>Données de contact et demandes : 3 ans après le dernier contact</li>
                <li>Données de facturation : 10 ans (obligation légale)</li>
                <li>Cookies : selon les paramètres de votre navigateur</li>
              </ul>
            </section>

            <section className="mb-12">
              <h2 className="text-2xl font-semibold mb-4">8. Vos droits</h2>
              <p>Conformément à la LPD suisse, vous disposez des droits suivants :</p>
              <ul>
                <li><strong>Droit d'accès :</strong> obtenir une copie de vos données personnelles</li>
                <li><strong>Droit de rectification :</strong> corriger des données inexactes</li>
                <li><strong>Droit à l'effacement :</strong> demander la suppression de vos données</li>
                <li><strong>Droit d'opposition :</strong> vous opposer au traitement de vos données</li>
                <li><strong>Droit à la portabilité :</strong> recevoir vos données dans un format structuré</li>
              </ul>
              <p>
                Pour exercer ces droits, contactez-nous à : 
                <a href="mailto:contact@homely-solutions.ch" className="text-[var(--sage)] hover:underline"> contact@homely-solutions.ch</a>
              </p>
            </section>

            <section className="mb-12">
              <h2 className="text-2xl font-semibold mb-4">9. Cookies</h2>
              <p>Notre site utilise des cookies pour :</p>
              <ul>
                <li><strong>Cookies essentiels :</strong> nécessaires au fonctionnement du site</li>
                <li><strong>Cookies analytiques :</strong> Google Analytics pour comprendre comment vous utilisez notre site</li>
                <li><strong>Cookies marketing :</strong> Facebook Pixel pour mesurer l'efficacité de nos campagnes</li>
              </ul>
              <p>Vous pouvez gérer vos préférences de cookies dans les paramètres de votre navigateur.</p>
            </section>

            <section className="mb-12">
              <h2 className="text-2xl font-semibold mb-4">10. Sécurité</h2>
              <p>
                Nous mettons en œuvre des mesures techniques et organisationnelles appropriées pour protéger vos 
                données personnelles contre tout accès non autorisé, modification, divulgation ou destruction. 
                Ces mesures incluent le chiffrement SSL/TLS, des pare-feu et des contrôles d'accès stricts.
              </p>
            </section>

            <section className="mb-12">
              <h2 className="text-2xl font-semibold mb-4">11. Transfert de données</h2>
              <p>
                Vos données sont principalement traitées en Suisse. En cas de transfert vers des pays tiers, 
                nous nous assurons que des garanties appropriées sont en place conformément à la LPD.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-semibold mb-4">12. Contact et réclamations</h2>
              <p>
                Pour toute question concernant cette politique de confidentialité ou pour exercer vos droits, 
                contactez-nous à : <a href="mailto:contact@homely-solutions.ch" className="text-[var(--sage)] hover:underline">contact@homely-solutions.ch</a>
              </p>
              <p className="mt-4">
                <strong>Homely Solutions Sàrl</strong><br />
                Rue Jean-André Venel 4<br />
                1400 Yverdon-les-Bains, Suisse
              </p>
              <p className="mt-4">
                Vous avez également le droit d'introduire une réclamation auprès du Préposé fédéral à la protection 
                des données et à la transparence (PFPDT) si vous estimez que le traitement de vos données n'est pas conforme.
              </p>
            </section>
          </div>
        </div>
      </main>
      
      <Footer />
    </div>
  );
};
